/**
 * @file 「AI 出力」(`/families/{familyId}/study/plans/{personId}/ask_records`) のクライアント。
 *
 * @what: 一覧 (要約のみ)、1件、選んで聞く、続けて聞く、答えを作り直す、やりとりへの評価 (1回)、
 *   終わりにする、削除である。
 * @why: 対象 (参照) と問いと答えを1件にして持つ。答えだけ残しても後で何に対する答えか分からないためである。
 *   誤った答えは消さずに残し、作り直したときは前の答えを畳んで出す。
 *   追記を受けなくなるのは、本人が終わりにしたときだけである。
 * @failure: ApiError を伝播する。評価の409 (already_reacted) は呼び出し側が文言に倒す。
 */

import { ApiError, apiEventStream, apiFetch } from "@/shared/api/client";
/** 役に立たなかった理由 (操作をまたいで同じ語彙を使う)。 */
export type AIReactionReason = "off_target" | "wrong" | "unclear" | "too_long" | "other";

export const AI_REACTION_REASON_LABELS: Record<AIReactionReason, string> = {
  off_target: "聞いたことと合っていない",
  wrong: "内容が間違っている",
  unclear: "説明がわかりにくい",
  too_long: "長すぎる",
  other: "その他",
};

/** AI の答えへの評価 (1つの答えに1回だけ)。 */
export interface StudyAIReaction {
  helpful: boolean;
  reasons?: AIReactionReason[];
  comment?: string;
  at: string;
}

export type AskTargetKind = "note" | "image" | "page" | "item" | "card";
export type AskStatus = "open" | "resolved" | "unresolved" | "closed";

export interface StudyAskTarget {
  ref: string;
  kind: AskTargetKind;
  source_id?: string;
  /** リーダーの連番である (紙に刷られた番号ではない)。 */
  page?: number;
  note_id?: string;
  problem_id?: string;
  label?: string;
  image_path?: string;
}

export interface StudyAskTurn {
  prompt: string;
  prompt_kind: "preset" | "free";
  preset_id?: string;
  answer: string;
  generation: {
    tool: string;
    model: string;
    prompt_version: string;
    prompt_hash: string;
    generated_at: string;
  };
  reaction?: StudyAIReaction;
  /** この答えを作るときに読んだ材料 (中身は持たず、種類と名前と量だけ)。 */
  materials?: StudyAskMaterial[];
  /** 作り直しの元になったやりとりの番号 (0始まり)。作り直しでなければ省かれる。 */
  retry_of?: number;
  /** 作り直しを頼むときに本人が書いた一言 (どこが違うか)。 */
  retry_note?: string;
  created_at: string;
}

/** 答えを作るときに読んだ材料の1行である。 */
export interface StudyAskMaterial {
  kind: "selected" | "answer" | "guide" | "note_image" | "result";
  label?: string;
  chars?: number;
  image?: boolean;
}

/** 材料の種類の名前 (画面に出す言葉)。 */
export const ASK_MATERIAL_LABELS: Record<StudyAskMaterial["kind"], string> = {
  selected: "選んだもの",
  answer: "本の解答",
  guide: "問の解説",
  note_image: "メモの画像",
  result: "解いた結果",
};

export interface StudyAskRecordSummary {
  id: string;
  targets: StudyAskTarget[];
  first_prompt: string;
  /** 最初のやりとりの操作の id (ai_explain / ai_grade)。自由入力なら省略される。 */
  first_preset?: string;
  turn_count: number;
  status: AskStatus;
  has_wrong_answer: boolean;
  created_at: string;
  updated_at: string;
}

export interface StudyAskRecord {
  id: string;
  learner_person_id: string;
  targets: StudyAskTarget[];
  /** 聞く前に本人が述べたこと (自信の程度・答え合わせの結果など)。AI の答えとは出どころが違う。 */
  self_report?: string;
  turns: StudyAskTurn[];
  status: AskStatus;
  has_wrong_answer: boolean;
  created_at: string;
  updated_at: string;
}

/** 操作の名前 (押したボタンと同じ言葉)。定型でないものは「AI に質問」にする。 */
export const ASK_PRESET_LABELS: Record<string, string> = {
  ai_explain: "AI 解説",
  ai_grade: "AI 採点",
};

/** 記録の操作の名前を返す (定型の id が未知でも落ちない)。 */
export function askPresetLabel(presetId: string | undefined): string {
  return (presetId && ASK_PRESET_LABELS[presetId]) || "AI に質問";
}

export const ASK_STATUS_LABELS: Record<AskStatus, string> = {
  open: "評価まち",
  resolved: "役に立った",
  unresolved: "役に立たなかった",
  closed: "終わりにした",
};

/** 1つの問いについて答えを作り直せる回数 (サーバと同じ値)。 */
export const ASK_RETRY_MAX = 3;

function basePath(familyId: string, personId: string): string {
  return `/families/${encodeURIComponent(familyId)}/study/plans/${encodeURIComponent(personId)}/ask_records`;
}

export async function fetchAskRecords(
  familyId: string,
  personId: string,
  signal?: AbortSignal,
): Promise<StudyAskRecordSummary[]> {
  const res = await apiFetch<{ records: StudyAskRecordSummary[] }>(
    basePath(familyId, personId),
    signal ? { signal } : {},
  );
  return res.records;
}

/** 「選んで聞く」の依頼である。選んだものと押したボタンを送り、答えの入った記録を受け取る。 */
export interface StudyAskRequest {
  refs: string[];
  preset_id: "ai_explain" | "ai_grade";
  question?: string;
  self_report?: string;
}

/**
 * 選んだものについて AI に聞き、答えを1件の記録にして返す。
 *
 * サーバが材料 (紙面の画像・メモの本文と画像) を集めてモデルに聞く。web はモデルを直接呼ばない
 * (キーが端末に出るため)。
 */
export async function postAskRecord(
  familyId: string,
  personId: string,
  body: StudyAskRequest,
): Promise<StudyAskRecord> {
  return apiFetch<StudyAskRecord>(basePath(familyId, personId), { method: "POST", body });
}

/**
 * 選んで聞き、答えを少しずつ受け取る。
 *
 * @what: 断片が届くたびに onChunk を呼び、最後に書けた記録を返す。
 * @why: 答えは十数秒かかる。読み始められる所から出すためである。
 * @failure: 答えが始まる前の失敗は postAskRecord と同じ ApiError になる。始まったあとの失敗は
 *   サーバが送る1行を持つ ApiError にする。
 */
export async function streamAskRecord(
  familyId: string,
  personId: string,
  body: StudyAskRequest,
  onChunk: (text: string) => void,
  signal?: AbortSignal,
): Promise<StudyAskRecord> {
  return streamAsk(`${basePath(familyId, personId)}/stream`, body, onChunk, signal);
}

/** 続けて聞く依頼と、答えを作り直す依頼である (retry_of_turn を渡すと作り直しになる)。 */
export interface StudyAskTurnRequest {
  question?: string;
  preset_id?: "ai_explain" | "ai_grade";
  /** 作り直す元のやりとりの番号 (0始まり)。渡すと問いは元から引き継ぐ。 */
  retry_of_turn?: number;
  /** 作り直しを頼むときに書く一言 (どこが違うか)。 */
  retry_note?: string;
}

/** 同じ対象のまま続けて聞く (または答えを作り直す)。答えは少しずつ受け取る。 */
export async function streamAskTurn(
  familyId: string,
  personId: string,
  recordId: string,
  body: StudyAskTurnRequest,
  onChunk: (text: string) => void,
  signal?: AbortSignal,
): Promise<StudyAskRecord> {
  return streamAsk(
    `${basePath(familyId, personId)}/${encodeURIComponent(recordId)}/turns/stream`,
    body,
    onChunk,
    signal,
  );
}

/** 少しずつ受け取る経路の中身である (作成と追記で同じ形の出来事が来る)。 */
async function streamAsk(
  path: string,
  body: unknown,
  onChunk: (text: string) => void,
  signal?: AbortSignal,
): Promise<StudyAskRecord> {
  let record: StudyAskRecord | null = null;
  let failed: ApiError | null = null;
  await apiEventStream(path, signal ? { body, signal } : { body }, (event) => {
    if (event.name === "chunk") {
      const parsed = JSON.parse(event.data) as { text?: string };
      if (typeof parsed.text === "string") onChunk(parsed.text);
      return;
    }
    if (event.name === "done") {
      record = JSON.parse(event.data) as StudyAskRecord;
      return;
    }
    if (event.name === "error") {
      const parsed = JSON.parse(event.data) as { message?: string };
      failed = new ApiError(500, parsed.message ?? "failed", parsed);
    }
  });
  if (failed !== null) throw failed;
  if (record === null) throw new ApiError(500, "the answer did not arrive");
  return record;
}

/**
 * 同じ対象のまま、続けて聞く。
 *
 * 対象は記録が持っているので選び直さない。本人が終わりにした記録には足せない (409)。
 */
export async function appendAskTurn(
  familyId: string,
  personId: string,
  recordId: string,
  body: StudyAskTurnRequest,
): Promise<StudyAskRecord> {
  return apiFetch<StudyAskRecord>(
    `${basePath(familyId, personId)}/${encodeURIComponent(recordId)}/turns`,
    { method: "POST", body },
  );
}

/**
 * 記録を閉じる (これで終わりにする)。
 *
 * 閉じると、続けて聞くのも作り直すのも受けない。既に閉じていても通る。
 */
export async function closeAskRecord(
  familyId: string,
  personId: string,
  recordId: string,
): Promise<StudyAskRecord> {
  return apiFetch<StudyAskRecord>(
    `${basePath(familyId, personId)}/${encodeURIComponent(recordId)}/close`,
    { method: "POST" },
  );
}

export async function fetchAskRecord(
  familyId: string,
  personId: string,
  recordId: string,
  signal?: AbortSignal,
): Promise<StudyAskRecord> {
  return apiFetch<StudyAskRecord>(
    `${basePath(familyId, personId)}/${encodeURIComponent(recordId)}`,
    signal ? { signal } : {},
  );
}

export async function postAskTurnReaction(
  familyId: string,
  personId: string,
  recordId: string,
  turnIndex: number,
  body: { helpful: boolean; reasons?: AIReactionReason[]; comment?: string },
): Promise<StudyAskRecord> {
  return apiFetch<StudyAskRecord>(
    `${basePath(familyId, personId)}/${encodeURIComponent(recordId)}/turns/${turnIndex}/reaction`,
    { method: "POST", body },
  );
}

export async function deleteAskRecord(
  familyId: string,
  personId: string,
  recordId: string,
): Promise<void> {
  await apiFetch<void>(`${basePath(familyId, personId)}/${encodeURIComponent(recordId)}`, {
    method: "DELETE",
  });
}

/**
 * 記録が絞り込みに当たった理由 (近いものが強い)。
 *
 * @why: 問ごとの解説の面では、問そのものに紐づく記録と、同じページというだけの記録が混ざる。
 *   同じ見た目で並べると、どこまでがその問の話か読み取れない (代表の判断、2026-09-09)。
 */
export type AskScope = "problem" | "note" | "page" | "source";

const ASK_SCOPE_RANK: Record<AskScope, number> = { problem: 3, note: 2, page: 1, source: 0 };

/** 行に付ける範囲の言葉である。問そのものに当たったときは何も出さない (それが既定のため)。 */
export const ASK_SCOPE_LABELS: Partial<Record<AskScope, string>> = {
  note: "このメモ",
  page: "同じページ",
};

/**
 * 対象が絞り込みに当たった理由を返す (当たらなければ null)。
 * sourceId を省くと、資料とページでは拾わず、メモと問だけで拾う (メモで絞る一覧の面)。
 */
export function askRecordScope(
  targets: readonly StudyAskTarget[],
  sourceId: string | undefined,
  page?: number,
  noteIds?: readonly string[],
  problemIds?: readonly string[],
): AskScope | null {
  let best: AskScope | null = null;
  const take = (s: AskScope) => {
    if (best === null || ASK_SCOPE_RANK[s] > ASK_SCOPE_RANK[best]) best = s;
  };
  for (const t of targets) {
    if (problemIds && problemIds.length > 0 && t.problem_id && problemIds.includes(t.problem_id)) {
      take("problem");
    }
    if (noteIds && t.note_id && noteIds.includes(t.note_id)) take("note");
    if (sourceId === undefined) continue;
    if (t.source_id === sourceId && page !== undefined && t.page === page) take("page");
    if (t.source_id === sourceId && page === undefined && problemIds === undefined) take("source");
  }
  return best;
}

/** 対象が「この資料のこのページ」「この問」「このメモ」のどれかに当たるかを返す。 */
export function askRecordMatches(
  targets: readonly StudyAskTarget[],
  sourceId: string | undefined,
  page?: number,
  noteIds?: readonly string[],
  problemIds?: readonly string[],
): boolean {
  return askRecordScope(targets, sourceId, page, noteIds, problemIds) !== null;
}

/** 記録から開ける、問ごとの解説の場所 (無ければ null)。 */
export function askGuideLink(
  targets: readonly StudyAskTarget[],
  familyId: string,
  exclude?: readonly string[],
): { href: string; problemId: string; sourceId: string } | null {
  for (const t of targets) {
    if (!t.problem_id || !t.source_id) continue;
    if (exclude && exclude.includes(t.problem_id)) continue;
    return {
      problemId: t.problem_id,
      sourceId: t.source_id,
      href: `/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(t.source_id)}/guides/${encodeURIComponent(t.problem_id)}`,
    };
  }
  return null;
}
